import { useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { useScrollArrows } from '../hooks/useScrollArrows';
import { useDragScroll } from '../hooks/useDragScroll';

interface CategoryPillItem {
    id: number;
    title: string;
    link: string;
}

interface CategoryPillsProps {
    categories: CategoryPillItem[];
    isVisible?: boolean;
}

/**
 * Returns a CSS mask-image value based on scroll overflow state.
 * Only applies edge fades where content actually overflows.
 */
function getMaskImage(
    canScrollLeft: boolean,
    canScrollRight: boolean,
): string | undefined {
    if (!canScrollLeft && !canScrollRight) return undefined;
    if (canScrollLeft && canScrollRight) {
        return 'linear-gradient(to right, transparent, black 48px, black calc(100% - 48px), transparent)';
    }
    if (canScrollLeft) {
        return 'linear-gradient(to right, transparent, black 48px, black 100%)';
    }
    return 'linear-gradient(to right, black, black calc(100% - 48px), transparent)';
}

/**
 * Pill shaped buttons used in the CategoryNavigationBar. Takes user to the category page when clicked.
 * Displays as a horizontally scrollable list with drag-to-scroll on desktop.
 * Supports keyboard navigation (ArrowLeft/Right, Home/End), scroll-snap alignment,
 * and native touch scrolling on mobile.
 * @param categories - The categories to display.
 * @param isVisible - Whether the pills are visible.
 */
export const CategoryPills = ({
    categories,
    isVisible = true,
}: CategoryPillsProps) => {
    const location = useLocation();
    const { scrollRef, canScrollLeft, canScrollRight, prefersReducedMotion } =
        useScrollArrows();

    // Enable drag-to-scroll on desktop, sharing the same scroll container ref
    useDragScroll({ externalRef: scrollRef });

    // Auto-scroll active pill into view on route change
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const rafId = requestAnimationFrame(() => {
            const activeEl = container.querySelector<HTMLElement>(
                '[aria-current="page"]',
            );
            if (activeEl) {
                activeEl.scrollIntoView({
                    inline: 'center',
                    behavior: prefersReducedMotion ? 'auto' : 'smooth',
                    block: 'nearest',
                });
            }
        });

        return () => cancelAnimationFrame(rafId);
    }, [location.pathname, scrollRef, prefersReducedMotion]);

    // Keyboard navigation: ArrowLeft/Right scroll by one pill, Home/End go to edges
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLUListElement>) => {
            const container = scrollRef.current;
            if (!container) return;

            const behavior: ScrollBehavior = prefersReducedMotion
                ? 'auto'
                : 'smooth';
            // Measure actual pill step from DOM to avoid hardcoded gap values
            const pills = container.querySelectorAll('li');
            const pillWidth =
                pills.length >= 2
                    ? (pills[1] as HTMLElement).offsetLeft -
                      (pills[0] as HTMLElement).offsetLeft
                    : (pills[0]?.getBoundingClientRect().width ?? 150);

            switch (e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    container.scrollBy({ left: pillWidth, behavior });
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    container.scrollBy({ left: -pillWidth, behavior });
                    break;
                case 'Home':
                    e.preventDefault();
                    container.scrollTo({ left: 0, behavior });
                    break;
                case 'End':
                    e.preventDefault();
                    container.scrollTo({
                        left: container.scrollWidth,
                        behavior,
                    });
                    break;
            }
        },
        [scrollRef, prefersReducedMotion],
    );

    if (!categories.length) {
        return null;
    }

    const maskImage = getMaskImage(canScrollLeft, canScrollRight);

    return (
        <div
            className={cn(
                'relative min-w-0 flex-1',
                isVisible
                    ? 'opacity-100 transition-all duration-300'
                    : 'w-0 overflow-hidden opacity-0 transition-all duration-300',
            )}
        >
            <ul
                ref={scrollRef as React.RefObject<HTMLUListElement>}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                className={cn(
                    'scrollbar-hide flex cursor-grab items-center gap-3 overflow-x-auto px-3 active:cursor-grabbing',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                )}
                aria-label="Category navigation"
                style={
                    maskImage
                        ? { maskImage, WebkitMaskImage: maskImage }
                        : undefined
                }
            >
                {categories.map((category) => (
                    <li key={category.id}>
                        <NavLink
                            to={category.link}
                            preventScrollReset
                            className={({ isActive }) =>
                                cn(
                                    'flex h-10.5 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-[1.3125rem] text-base transition-colors',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                                    isActive
                                        ? 'bg-[#161616] text-white hover:bg-[#2a2a2a] active:bg-[#333333]'
                                        : 'bg-[#f2f2f2] text-[#323232] hover:bg-[#e8e8e8] active:bg-[#dedede]',
                                )
                            }
                        >
                            {category.title}
                        </NavLink>
                    </li>
                ))}
            </ul>
        </div>
    );
};
